import { DatePicker, type DatePickerProps } from "@msys/ui";
import {
  FormControl,
  FormHelperText,
  type TextFieldProps,
} from "@mui/material";
import { useFormikContext } from "formik";
import { get, isString } from "lodash-es";
import { Moment } from "moment";
import { type ChangeEvent } from "react";
import { browserHasInputDate } from "../../featureDetection.js";

export interface Props extends Omit<DatePickerProps, "value" | "onChange"> {
  id?: string;
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
  name: string;
  placeholder?: string;
  value?: Moment | null;
  onChange?: (
    e: ChangeEvent<HTMLInputElement> | null,
    newValue: Moment | null
  ) => void | Promise<void>;
  textFieldProps?: Omit<TextFieldProps, "value" | "defaultValue" | "onChange">;
}

export const DatePickerField = ({
  fullWidth = true,
  name,
  value,
  onChange,
  textFieldProps,
  placeholder,
  ...otherProps
}: Props) => {
  const { errors, values, setFieldValue, isSubmitting } =
    useFormikContext<Record<string, any>>();

  const error = get(errors, name);

  return (
    <FormControl
      fullWidth={fullWidth}
      required={otherProps.required}
      error={Boolean(error)}
      disabled={otherProps.disabled ?? isSubmitting}
    >
      <DatePicker
        browserHasInputDate={browserHasInputDate}
        value={value ?? get(values, name) ?? null}
        onChange={
          onChange ??
          (async (e, date) => {
            if (otherProps.isClearable || (date && date.isValid())) {
              await setFieldValue(
                name,
                date ? (date.isValid() ? date : null) : null
              );
            }
          })
        }
        textFieldProps={{
          name,
          placeholder,
          error: Boolean(error),
          disabled: otherProps.disabled ?? isSubmitting,
          ...textFieldProps,
        }}
        {...otherProps}
        disabled={otherProps.disabled ?? isSubmitting}
      />
      {Boolean(error) && isString(error) && (
        <FormHelperText>{error}</FormHelperText>
      )}
    </FormControl>
  );
};
